import { useEffect, useRef, useState } from 'react';


interface StarRatingProps {
  value: number;
  defaultValue?: number;
  onChange?: (value: number | null) => void;

  size?: number;
  max?: number;
  precision?: number;

  color?: string;
  borderColor?: string;
  defaultValueColor?: string;
}


export const StarRating: React.FC<StarRatingProps> = ({
  value,
  defaultValue,
  onChange,
  size = 22,
  color = '#ffde00',
  borderColor = '#9d9fa3',
  defaultValueColor = '#a7a9ad',
  max = 5,
  precision = 0.5,
}) => {

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const valueToUse = value ? value : defaultValue ?? 0;
  const displayValue = hoverValue ?? valueToUse;


  const handleClick = (e: React.MouseEvent) => {
    if (!onChange || !containerRef.current) return;

    const { left, width } = containerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - left;
    const totalUnits = max / precision;
    const unitWidth = width / totalUnits;
    const clickedValue = Math.min(
      max,
      Math.max(precision, Math.ceil(relativeX / unitWidth) * precision)
    );

    if (clickedValue === value) {
      onChange(null);
      setHoverValue(null);
    } else {
      onChange(clickedValue);
    }
  };


  const handleMouseMove = (e: React.MouseEvent) => {
    if (!onChange || !containerRef.current) return;

    const { left, width } = containerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - left;
    const totalUnits = max / precision;
    const unitWidth = width / totalUnits;
    const newValue = Math.min(
      max,
      Math.max(precision, Math.ceil(relativeX / unitWidth) * precision)
    );
    setHoverValue(newValue);
  };


  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const isInside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      if (!isInside) {
        setHoverValue(null);
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, []);


  const hoveredScale = 1.15;


  const starColor = !value && !hoverValue && defaultValue && defaultValueColor ? defaultValueColor : color;


  const renderStar = (i: number) => {

    const starValue = i + 1;
    const full = displayValue >= starValue;
    const partial = displayValue >= starValue - precision && displayValue < starValue;

    const isHoveredStar =
      hoverValue !== null &&
      hoverValue > i &&
      hoverValue <= starValue;

    return (
      <div
        key={i}
        style={{
          width: size,
          height: size,
          position: 'relative',
          display: 'inline-block',
          transform: isHoveredStar ? `scale(${hoveredScale})` : 'scale(1)',
          transition: 'transform 120ms ease',
        }}
      >

        {full && (
          <svg
            viewBox="0 0 24 24"
            width={size}
            height={size}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              fill: starColor,
              stroke: starColor,
              strokeWidth: 1.5,
            }}
          >
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        )}

        {partial && (
          <>
            <svg
              viewBox="0 0 24 24"
              width={size}
              height={size}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                fill: starColor,
                stroke: starColor,
                strokeWidth: 1.5,
                clipPath: 'inset(0 50% 0 0)',
              }}
            >
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>

            <svg
              viewBox="0 0 24 24"
              width={size}
              height={size}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                fill: 'none',
                stroke: borderColor,
                strokeWidth: 1.5,
                clipPath: 'inset(0 0 0 50%)',
              }}
            >
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>
          </>
        )}

        {!full && !partial && (
          <svg
            viewBox="0 0 24 24"
            width={size}
            height={size}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              fill: 'none',
              stroke: borderColor,
              strokeWidth: 1.5,
            }}
          >
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        gap: 4,
        userSelect: 'none',
        cursor: onChange ? 'pointer' : 'default',
      }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      {Array.from({ length: max }).map((_, i) => renderStar(i))}
    </div>
  );
};
